import React, { useRef } from 'react'
import { useAppStore } from '../../store/useAppStore'
import { Topbar } from '../topbar'
import { Sidebar } from '../sidebar'
import { ExportModal } from '../export'
import { useExport } from '../../features/export/hooks'
import { handleExportError } from '../../features/export/utils'
import { showSuccess, showError } from '../../store/uiSlice'
import { useTranslation } from 'react-i18next'
import { EditorContainer, EditorContainerRef } from '../editor'
import { FullScreenPreviewModal } from '../preview/FullScreenPreviewModal'

/**
 * 主布局组件
 * 包含顶部栏、侧边栏和主内容区域
 */
export const MainLayout: React.FC = () => {
  const { t } = useTranslation()
  const { isSidebarVisible, isExportModalOpen, toggleExportModal, currentDocument, toggleSidebar } = useAppStore()
  const editorContainerRef = useRef<EditorContainerRef>(null)
  const {
    isExporting,
    progress,
    exportContent,
    cancelExport
  } = useExport()

  // 处理导出
  const handleExport = async (options: any) => {
    try {
      // 获取当前文档内容
      const content = currentDocument?.content || ''

      if (!content.trim()) {
        showError('导出失败', '文档内容为空，无法导出')
        return
      }

      // 获取预览区域的 DOM 元素
      const previewElement = editorContainerRef.current?.getPreviewElement() || undefined

      await exportContent(content, options, previewElement)
      showSuccess('导出成功', `文档已成功导出为 ${options.format.toUpperCase()} 格式`)
      toggleExportModal()
    } catch (error) {
      const exportError = handleExportError(error)
      showError('导出失败', exportError.message)
    }
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* 顶部栏 */}
      <Topbar />

      {/* 主内容区域 */}
      <div className="flex flex-1 pt-14 relative overflow-hidden">
        {/* 侧边栏 */}
        <Sidebar />

        {/* 移动端侧边栏遮罩 */}
        {isSidebarVisible && (
          <div 
            className="fixed inset-0 bg-black/20 z-30 md:hidden"
            onClick={toggleSidebar}
          />
        )}

        {/* 主内容 */}
        <main
          className={`
            flex-1 overflow-auto transition-all duration-200
            ${isSidebarVisible ? 'md:ml-64' : 'ml-0'}
          `}
        >
          <EditorContainer ref={editorContainerRef} />
        </main>
      </div>

      {/* 导出模态框 */}
      <ExportModal
        isVisible={isExportModalOpen}
        onClose={toggleExportModal}
        onExport={handleExport}
        documentTitle={currentDocument?.title}
        content={currentDocument?.content}
        isExporting={isExporting}
        exportProgress={progress}
        onCancel={cancelExport}
      />

      {/* 全屏预览模态框 */}
      <FullScreenPreviewModal />

      {/* 页脚 */}
      <footer className="py-2 px-4 text-center text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="container mx-auto">
          <div className="text-xs">
            <span>{t('footer.copyright')}</span>
          </div>
        </div>
      </footer>
    </div>
  )
}